import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Link2, Loader2, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { engine } from "@/audio/engine";
import { encodeWav, computePeaks } from "@/audio/dsp";

// /api/s/:id serves social preview tags, then forwards to /listen/:id
const shareUrl = (id) => `${process.env.REACT_APP_BACKEND_URL}/api/s/${id}`;

const copy = (id) => navigator.clipboard?.writeText(shareUrl(id)).then(() => toast.success("Link copied"), () => toast(shareUrl(id)));

const ShareRow = ({ s, onDelete }) => (
  <div className="flex items-center gap-2 border border-[#352d40] px-3 py-2" data-testid="share-item">
    <div className="min-w-0 flex-1">
      <p className="text-sm text-stone-200 truncate">{s.title}</p>
      <a href={shareUrl(s.share_id)} target="_blank" rel="noreferrer" className="text-[11px] font-mono-vox text-amber-300/80 truncate block hover:underline" data-testid="share-item-link">
        {shareUrl(s.share_id)}
      </a>
    </div>
    <span className="eyebrow">{s.plays} plays</span>
    <button onClick={() => copy(s.share_id)} className="p-1.5 text-stone-400 hover:text-amber-200" aria-label="Copy link" data-testid="share-item-copy"><Copy size={14} /></button>
    <button onClick={onDelete} className="p-1.5 text-stone-500 hover:text-[#E11D48]" aria-label="Delete link" data-testid="share-item-delete"><Trash2 size={14} /></button>
  </div>
);

export const ShareDialog = ({ open, onOpenChange, project, artistDefault }) => {
  const [title, setTitle] = useState("");
  const [artist, setArtist] = useState("");
  const [shares, setShares] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTitle(project.name);
    setArtist(artistDefault || "");
    api.listShares(project.id).then(setShares).catch(() => {});
  }, [open, project.id, project.name, artistDefault]);

  const create = async () => {
    setBusy(true);
    try {
      const buf = await engine.renderMix(project, false, 32000);
      const { file_id } = await api.uploadAudio(encodeWav(buf), "shared-mix.wav");
      const s = await api.createShare({ file_id, project_id: project.id, title: title.trim() || project.name, artist: artist.trim(), duration: buf.duration, peaks: Array.from(computePeaks(buf, 400)) });
      setShares((cur) => [s, ...cur]);
      copy(s.share_id);
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy(false);
  };

  const remove = async (id) => {
    await api.deleteShare(id).catch((e) => toast.error(errMsg(e)));
    setShares((cur) => cur.filter((s) => s.share_id !== id));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="share-dialog" className="bg-[#141018] border-[#5a1e28] rounded-none max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-gothic uppercase tracking-wider text-amber-100/90 flex items-center gap-2"><Link2 size={18} className="text-[#D99B43]" /> Share this mix</DialogTitle>
          <DialogDescription className="font-cormorant italic text-lg text-stone-300">Renders the current mix and creates a public listen page anyone with the link can open.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="eyebrow block mb-1.5">Song title</span>
            <input data-testid="share-title-input" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={80} className="w-full bg-[#0f0d11] border border-[#352d40] px-3 py-2 text-sm outline-none focus:border-[#D99B43]" />
          </label>
          <label className="block">
            <span className="eyebrow block mb-1.5">Artist</span>
            <input data-testid="share-artist-input" value={artist} onChange={(e) => setArtist(e.target.value)} maxLength={60} className="w-full bg-[#0f0d11] border border-[#352d40] px-3 py-2 text-sm outline-none focus:border-[#D99B43]" />
          </label>
        </div>
        <button data-testid="share-create-btn" onClick={create} disabled={busy} className="btn-crimson w-full justify-center">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Link2 size={14} />} {busy ? "Rendering & uploading…" : "Create share link"}
        </button>
        {shares.length > 0 && (
          <div className="space-y-2 max-h-56 overflow-y-auto" data-testid="share-list">
            <p className="eyebrow">Links for this project</p>
            {shares.map((s) => <ShareRow key={s.share_id} s={s} onDelete={() => remove(s.share_id)} />)}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
